import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { styled } from '@mui/material/styles';
import { Grid, Paper, Tooltip, TooltipProps, Typography } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import { tooltipClasses } from '@mui/material/Tooltip';

import { CalendarToolbar } from './calendar-toolbar';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import { useNavigate } from 'react-router-dom';

const styling = {
  paperStyle: { borderRadius: '20px' },
};

interface CalendarComponent {
  workorderEvent: any;
  height?: number;
  colorCode?: any;
  isLoading: boolean;
  isMobileView?: any;
  calendarView?: string;
  position?: string;
}

// const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
//   <Tooltip {...props} arrow classes={{ popper: className }} />
// ))(({ theme }) => ({
//   [`& .${tooltipClasses.arrow}`]: {
//     color: '#0000b5', // This sets the color of the arrow
//     '&:before': {
//       borderColor: `${'#0000b5'} transparent transparent transparent`, // Ensure the border is set correctly
//       borderTopColor: '#0000b5',
//     },
//   },
//   [`& .${tooltipClasses.tooltip}`]: {
//     backgroundColor: '#ffffff',
//     minWidth: 360,
//     color: '#000000',
//     boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
//   },
// }));

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#ffffff', // Match the tooltip background color (white)
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
    zIndex: '10',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#ffffff',
    minWidth: 360,
    color: '#000000',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)', // Add shadow to the tooltip
  },
}));

const reduceTitleSize = (view: string, title: string) => {
  let truncatedTitle = title;
  if (view === 'dayGridMonth' || view === 'timeGridWeek') {
    truncatedTitle = title?.length > 25 ? title.slice(0, 25 - 2) + '...' : title;
  }

  return truncatedTitle ?? '';
};

const hasTimeComponent = (dateString: any) => {
  const dateTimeFormat = 'MM/DD/YYYY hh:mm A';
  const dateFormat = 'MM/DD/YYYY';

  const isDateTime = moment(dateString, dateTimeFormat, true).isValid();
  const isDateOnly = moment(dateString, dateFormat, true).isValid();

  return isDateTime && !isDateOnly;
};

const HoverWorkOrder = ({
  inspectorName,
  customer,
  project,
  lotAddress,
  serviceName,
  workorder,
  workorderStatus,
  scheduleDate
}: {
  inspectorName: string,
  customer: string,
  project: string,
  lotAddress: string,
  serviceName: string,
  workorder: string,
  workorderStatus: string,
  scheduleDate: any,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const handleClickWorkorderNumber = (workorder: string) => {
    navigate(`/workorders?searchType=workorders&searchText=${workorder}`);
  };

  return (
    <div style={{ padding: '0 0px 10px 10px' }}>
      <Grid container spacing={1}>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Typography variant="body2">Inspector: {inspectorName}</Typography>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Typography variant="body2">Customer: {customer}</Typography>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Typography variant="body2">Project: {project}</Typography>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Typography variant="body2">Lot #: {lotAddress}</Typography>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Typography variant="body2">Service: {serviceName}</Typography>
        </Grid>
        {/* <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Typography variant="body2">Work Order: {workorder}</Typography>
        </Grid> */}
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Typography variant="body2">
            Work Order:{' '}
            <Typography
              component="span"
              variant="body2"
              style={{
                color: '#0000b5',
                textDecoration: isHovered ? 'underline' : 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onClick={() => handleClickWorkorderNumber(workorder)}
            >
              {workorder}
            </Typography>
          </Typography>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Typography variant="body2">Status: {workorderStatus}</Typography>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Typography variant="body2">Schedule Date: {scheduleDate}</Typography>
        </Grid>
      </Grid>
    </div>
  );
};

export const CalendarComponent = ({
  workorderEvent,
  height,
  colorCode,
  isLoading,
  isMobileView,
  calendarView,
  position,
}: CalendarComponent) => {
  const calendarRef = useRef<FullCalendar>(null);
  const [view, setView] = useState(calendarView ?? 'dayGridMonth');
  const [tooltipPosition, setTooltipPosition] = useState<string>(position ?? 'right');
  const [date, setDate] = useState(moment().toDate());
  const [events, setEvent] = useState<any[]>([]);

  useEffect(() => {
    if (workorderEvent) {
      const updatedData = workorderEvent.map((inspector: any) => ({
        ...inspector,
        title: reduceTitleSize(view, inspector?.title),
        start: hasTimeComponent(inspector?.scheduledDate)
          ? moment(inspector?.scheduledDate, 'MM/DD/YYYY hh:mm A').format('YYYY-MM-DDTHH:mm:ss')
          : moment(inspector?.scheduledDate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        extendedProps: { backgroundColor: colorCode ? colorCode[Number(inspector?.inspectorID)] : '#0000b5' },
        color: colorCode ? colorCode[Number(inspector?.inspectorID)] : '#0000b5',
      }));
      setEvent(updatedData);
    }
  }, [workorderEvent, colorCode, view]);

  const handleDatePrev = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.prev();
      setDate(calendarApi.getDate());
    }
  };

  const handleDateToday = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.today();
      setDate(calendarApi.getDate());
    }
  };

  const handleDateNext = () => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.next();
      setDate(calendarApi.getDate());
    }
  };

  const handleViewChange = (newView: any) => {
    const calendarEl = calendarRef.current;
    if (calendarEl) {
      const calendarApi = calendarEl.getApi();
      calendarApi.changeView(newView);
      setTooltipPosition(newView == 'timeGridDay' || newView == 'listWeek' ? 'bottom-start' : 'right');
      setView(newView);
    }
  };

  const renderEventContent = (eventInfo: any) => {
    const inspectorName = eventInfo.event.extendedProps.inspectorName;
    const customer = eventInfo.event.extendedProps.customerName;
    const project = eventInfo.event.extendedProps.projectName;
    const lotAddress = eventInfo.event.extendedProps.lotAddress;
    const serviceName = eventInfo.event.extendedProps.serviceName;
    const workOrder = eventInfo.event.extendedProps.workOrderNumber;
    const scheduleDate = eventInfo.event.extendedProps.scheduledDate;
    const workorderStatus = eventInfo.event.extendedProps.workOrderStatus;
    const paddingValue = eventInfo.timeText ? 5 : 0;
    return (
      <CustomTooltip
        placement={tooltipPosition as 'bottom-start' | 'right'}
        title={<HoverWorkOrder
          inspectorName={inspectorName}
          customer={customer}
          project={project}
          lotAddress={lotAddress}
          serviceName={serviceName}
          workorder={workOrder}
          workorderStatus={workorderStatus}
          scheduleDate={scheduleDate}
        />}
      >
        <div
          style={{
            fontSize: 12,
            marginBottom: 0,
            paddingLeft: 5,
            width: '100%',
            cursor: 'pointer',
            backgroundColor: eventInfo.event.extendedProps.backgroundColor,
          }}
        >
          <p style={{ color: 'white', paddingLeft: paddingValue }}>
            <b style={{ color: 'white', paddingRight: 3 }}>{eventInfo.timeText}</b>
            {eventInfo.event.title}
          </p>
        </div>
      </CustomTooltip>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        <CalendarToolbar
          date={date}
          calendarView={calendarView}
          isMobileView={isMobileView}
          onDateNext={handleDateNext}
          onDatePrev={handleDatePrev}
          onDateToday={handleDateToday}
          onViewChange={handleViewChange}
        />
      </Grid>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        <Paper elevation={0} style={styling.paperStyle}>
          <LoadingIndicator isLoading={isLoading} />
          <FullCalendar
            initialDate={date}
            initialView={view}
            allDayMaintainDuration
            selectMirror={true}
            dayMaxEvents={true}
            headerToolbar={false}
            weekends
            rerenderDelay={10}
            height={height}
            events={events}
            ref={calendarRef}
            eventContent={renderEventContent}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};
